import { createFileRoute } from "@tanstack/react-router";
import { FileSignature, ShieldCheck, Wand2, PenLine, Archive, CreditCard, Copy } from "lucide-react";
import { AiGeneratorPage } from "@/components/marketing/AiGeneratorPage";

export const Route = createFileRoute("/ai/contract-generator")({
  head: () => ({
    meta: [
      { title: "Free AI Contract Generator — Draft Legal Contracts in Seconds | banana.cards" },
      { name: "description", content: "Generate professional contracts with AI in seconds — NDAs, freelance agreements, service contracts and more. Free to try, e-sign ready, lawyer-reviewed templates." },
      { property: "og:title", content: "Free AI Contract Generator — banana.cards" },
      { property: "og:description", content: "Generate professional contracts with AI in seconds. NDAs, freelance, service, retainer — free to try." },
      { property: "og:url", content: "https://banana.cards/ai/contract-generator" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/ai/contract-generator" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AI Contract Generator",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
      },
    ],
  }),
  component: Page,
});

const EXAMPLES = [
  "Mutual NDA between a freelance designer and a SaaS startup, governed by California law",
  "Web design service contract: 8 weeks, $12,000, 50% deposit, 2 rounds of revisions",
  "Photography retainer for a wedding in Lisbon, 8 hours coverage, online gallery delivery",
  "Independent contractor agreement for a part-time marketing consultant, $80/hour, 6 month term",
  "Equipment rental contract for DJ gear, weekend rental, $400 deposit, damage clause",
];

export function Page() {
  return (
    <AiGeneratorPage
      eyebrow="AI Contract Generator"
      title="Draft a contract"
      italic="in seconds."
      description="Tell us what you need. Banana drafts a clean, signable contract you can edit, brand and send for e-signature — built on lawyer-reviewed templates."
      headline="What kind of contract would you like to create?"
      placeholders={EXAMPLES}
      linkedFromHref="/platform/contracts"
      linkedFromLabel="Banana Contracts & e-Signature"
      sections={[
        { icon: Wand2, title: "Plain-English prompts", desc: "Describe the deal in your own words. Banana picks the right template, parties and clauses." },
        { icon: FileSignature, title: "E-sign in one click", desc: "Send the generated PDF for signature directly from your Banana workspace — no other tool needed." },
        { icon: ShieldCheck, title: "Legally sound", desc: "Built on lawyer-reviewed templates. Compliant with US ESIGN, UETA and EU eIDAS." },
      ]}
      capabilities={[
        "NDAs, MSAs, SOWs, freelance & retainer agreements",
        "Auto-fills parties, dates, prices and jurisdictions",
        "Conditional clauses (late payment, IP, kill fee)",
        "Editable in a rich text editor before sending",
        "Tamper-evident audit trail on signed PDFs",
        "Stored in your private contract vault",
        "Reusable templates per client or category",
        "Export to .pdf or .docx",
      ]}
      howItWorks={[
        { title: "Describe the deal", desc: "Type a sentence or two — parties, scope, price, term." },
        { title: "AI drafts the contract", desc: "Picks the matching template and personalizes every clause." },
        { title: "Edit & brand", desc: "Tweak any line, add your logo, attach exhibits." },
        { title: "Send for e-signature", desc: "Signers tap a link on any device — no account needed." },
      ]}
      faq={[
        { q: "Is the generated contract legally binding?", a: "Yes — Banana drafts on top of lawyer-reviewed templates and any signature collected through the platform is compliant with the US ESIGN Act, UETA and EU eIDAS regulation. That said, for high-stakes deals we always recommend a final review by your own attorney." },
        { q: "What kinds of contracts can the AI generate?", a: "Mutual and one-way NDAs, MSAs, SOWs, freelance and consulting agreements, service contracts, retainers, photography and event contracts, equipment rental, employment offers, partnership and reseller agreements, waivers and consent forms — anything you'd normally send as a PDF." },
        { q: "Do I need a Banana account to try it?", a: "No — the generator above is free to use right now. You'll only need to sign up if you want to save the contract, edit it in our document editor, or send it out for e-signature." },
        { q: "Can I use my own template?", a: "Yes — paid plans let you upload your own .docx or .pdf as a base template, then use the AI to personalize it per client with placeholders like {{client_name}}, {{amount}} and {{start_date}}." },
        { q: "What languages does the contract generator support?", a: "The generator can draft and translate contracts in 50+ languages including Spanish, French, German, Italian, Portuguese, Dutch, Mandarin, Japanese, Korean, Arabic and Hindi — just write your prompt in any of those languages." },
      ]}
      flow={{
        steps: [
          {
            question: "What kind of contract?",
            multi: false,
            options: ["NDA", "Freelance / Contractor", "MSA / SOW", "Employment", "Photography", "Rental", "Partnership", "Other"],
          },
          {
            question: "Who's involved?",
            multi: false,
            options: ["Just me & one party", "Multiple signers", "Company ↔ Company", "Company ↔ Individual"],
          },
          {
            question: "What else should it include?",
            options: ["Payment terms", "IP assignment", "Confidentiality", "Termination clause", "Governing law", "Late fees"],
          },
        ],
        loadingMessages: [
          "Reviewing your requirements…",
          "Selecting the right template…",
          "Drafting clauses…",
          "Applying jurisdiction rules…",
          "Formatting for e-signature…",
          "Adding audit-trail metadata…",
          "Almost done — polishing language…",
        ],
        readyTitle: "Your contract is ready.",
        readyBody: "Banana AI drafted a complete, signable contract based on your answers. Sign up to view, edit, brand and send it for e-signature.",
        ctaLabel: "Sign up to view it",
        ctaHref: "https://app.banana.cards/signup",
        nextThings: [
          { icon: PenLine, title: "E-sign in one click", desc: "Send for signature from any device — no signer account needed." },
          { icon: Archive, title: "Store in a vault", desc: "Every contract archived with a tamper-evident audit trail." },
          { icon: CreditCard, title: "Collect payment on signing", desc: "Attach a deposit or full payment link to any contract." },
          { icon: Copy, title: "Reuse as a template", desc: "Save this draft as a template per client or category." },
        ],
      }}
    />
  );
}
